import { Form, Input } from "antd";
import React, { useState } from "react";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { passwordValidator } from "../../utils/passwordValidator";

const CustomInput = ({
  label,
  name,
  type,
  defaultValue,
  errorMessage,
  required,
  isEmail,
  disable,
  rules = [],
  onChange,
  noValidation,
  ...rest
}) => {
  const [show, setShow] = useState(false);
  const form = Form.useFormInstance(); // Get form instance

  // Handle input change with sanitization (blocks typing and pasting)
  const handleInputChange = (e) => {
    // Sanitize the value to remove restricted characters
    const sanitizedValue = e.target.value.replace(/['""`''""]/g, "");

    // Update the actual input value
    e.target.value = sanitizedValue;

    // Update the form field value
    if (form && name) {
      form.setFieldValue(name, sanitizedValue);
    }

    // Handle password confirmation validation
    if (name === "confirmPassword") {
      const newPassword = form.getFieldValue("password");
      if (sanitizedValue && newPassword !== sanitizedValue) {
        form.setFields([
          {
            name: "confirmPassword",
            errors: ["Passwords do not match!"],
          },
        ]);
      } else {
        form.setFields([
          {
            name: "confirmPassword",
            errors: [],
          },
        ]);
      }
    }

    // Create a new event object with sanitized value for onChange callback
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: sanitizedValue,
      },
    };

    onChange?.(syntheticEvent); // Call the passed onChange function
  };

  // Handle onChange for number input to prevent negative values
  const handleNumberChange = (e) => {
    const { value } = e.target;
    if (value === "" || Number(value) >= 0) {
      handleInputChange(e);
    }
  };

  // Custom validation rule for email input
  const emailValidationRule = isEmail
    ? {
        type: "email",
        message: "Please enter a valid email address",
      }
    : null;

  // Default rules (can be overridden by parent)
  const defaultRules = [
    {
      required: required,
      message: `${label} is required`,
    },
    ...(emailValidationRule ? [emailValidationRule] : []),
    ...((name === "password" || name === "Password") && !noValidation
      ? [
          {
            validator: (_, value) => {
              if (!value || passwordValidator(value)) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error(
                  "Password must be at least 8 characters long and include a combination of uppercase and lowercase letters, numbers, and special characters (e.g. !, @, #, $, %)"
                )
              );
            },
          },
        ]
      : []),
  ];

  return (
    <Form.Item
      label={
        required ? (
          <span>
            {label}
            <span style={{ color: "red" }}>*</span>
          </span>
        ) : (
          label
        )
      }
      name={name}
      initialValue={defaultValue}
      rules={rules.length ? rules : defaultRules}
      validateStatus={errorMessage ? "error" : undefined}
      help={errorMessage}
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      required={false}
    >
      {type === "password" ? (
        <Input.Password
          type={show ? "text" : "password"}
          autoComplete="off"
          spellCheck="false"
          onChange={handleInputChange}
          iconRender={(visible) =>
            visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
          }
          {...rest}
        />
      ) : type === "number" ? (
        <Input
          disabled={disable}
          type={type}
          defaultValue={defaultValue}
          onChange={handleNumberChange}
          min={0}
          style={{ color: disable ? "#000000" : "initial" }}
          {...rest}
          onKeyDown={(e) => {
            // Prevent 'e', 'E', '+', '-' keys
            if (["e", "E", "+", "-"].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onPaste={(e) => {
            // Prevent pasting negative numbers or scientific notation
            const pastedText = e.clipboardData.getData("text");
            if (
              pastedText.includes("-") ||
              pastedText.includes("e") ||
              pastedText.includes("E")
            ) {
              e.preventDefault();
            }
          }}
        />
      ) : (
        <Input
          disabled={disable}
          type={type}
          onChange={handleInputChange}
          className={`rounded-[4px] ${disable ? "!bg-[#fafbfc]" : ""}`}
          style={{
            color: disable ? "#000000" : "initial",
          }}
          {...rest}
        />
      )}
    </Form.Item>
  );
};

export default CustomInput;
